import React from "react";
import { useNavigate } from "react-router-dom";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { durationLabel, learnerName, personName, sessionStatusChip } from "../../../utils/studentDetail";
import { dateTimeLines } from "../../../utils/kitPresentation";
import StatusChip from "../../../components/kit/StatusChip";
import { EyeButton, PersonCell, TableMessageRow, TwoLineCell } from "../../../components/kit/TableParts";

// The sessions table (044 D35) as Figma draws it. On the student's tabs: Date · Learner ·
// Teacher · Subject · Duration · Status (Sessions frame 10786:43184). On the child page
// (`forChild`, 10764:83360): no Learner, and the subject sits under the teacher. On the tutor
// page (`forTutor`, 047 step 4): Date · Student · Learner · Subject · Duration · Status, the
// student from `usersById` and each row's "Self" in that student's colours. The row and its eye
// open the lesson page (`/lessons/<id>`, 047 D92), which carries the staff doors (044 D9; 047 D72).
const SessionsTable = ({
  rows,
  childrenById = {},
  usersById = {},
  holder,
  timeZone,
  loading,
  error,
  onRetry,
  emptyText = "No sessions.",
  forChild = false,
  forTutor = false,
}) => {
  const navigate = useNavigate();
  const columns = forChild
    ? ["Date", "Teacher", "Duration", "Status", "Actions"]
    : forTutor
    ? ["Date", "Student", "Learner", "Subject", "Duration", "Status", "Actions"]
    : ["Date", "Learner", "Teacher", "Subject", "Duration", "Status", "Actions"];
  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell key={c}>{c}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading || error || rows.length === 0 ? (
            <TableMessageRow colSpan={columns.length} loading={loading} error={error} onRetry={onRetry} emptyText={emptyText} />
          ) : (
            rows.map((b) => {
              // The tutor's page reads the time in the tutor's zone; a student's, in the learner's.
              const when = dateTimeLines(b.startTimeUTC, { timeZone: forTutor ? timeZone : b.studentTimeZone || timeZone, weekday: true });
              const teacher = usersById[b.teacherId];
              const student = forTutor ? usersById[b.studentId] : null;
              const account = forTutor ? { id: b.studentId, name: personName(student) } : holder;
              const isChild = b.learner?.kind === "child";
              return (
                <TableRow key={b.id} hover onClick={() => navigate(`/lessons/${b.id}`)} sx={{ cursor: "pointer" }}>
                  <TableCell>
                    <TwoLineCell top={when ? when.date : "-"} bottom={when?.time} />
                  </TableCell>
                  {forTutor && (
                    <TableCell>
                      {student ? <PersonCell name={personName(student)} colorKey={b.studentId} /> : <TwoLineCell top="-" />}
                    </TableCell>
                  )}
                  {!forChild && (
                    <TableCell>
                      {/* A "Self" row wears the account holder's initials and colour (plan P5). */}
                      <PersonCell
                        name={learnerName(b, childrenById)}
                        avatarName={isChild ? learnerName(b, childrenById) : account?.name || "Self"}
                        colorKey={isChild ? b.learner.childId : account?.id}
                      />
                    </TableCell>
                  )}
                  {!forTutor && (
                    <TableCell>
                      {teacher ? (
                        <PersonCell name={personName(teacher)} colorKey={b.teacherId} sub={forChild ? b.subject : undefined} />
                      ) : (
                        <TwoLineCell top="-" bottom={forChild ? b.subject : undefined} />
                      )}
                    </TableCell>
                  )}
                  {!forChild && <TableCell>{b.subject || "-"}</TableCell>}
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{durationLabel(b.duration)}</TableCell>
                  <TableCell>
                    <StatusChip {...sessionStatusChip(b)} />
                  </TableCell>
                  {/* The row opens the lesson; the eye must not open it a second time. */}
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <EyeButton to={`/lessons/${b.id}`} label="Open lesson" />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SessionsTable;
